import { pantryColors } from '@pantry/ui'
import {
  STORE_BADGE_COLOR,
  STORE_SHORT_TYPE_NAMES,
  STORE_TYPES,
} from '~/constants/store'

/**
 * Background colour per POS type, mirroring the legacy store badge
 * (`STORE_BADGE_COLOR`). Cova/Greenline reuse the app constant; Flowhub and
 * Lightspeed are supplemented here since the legacy map omits them.
 */
const POS_BADGE_COLORS: Record<string, string> = {
  [STORE_TYPES.COVA]: STORE_BADGE_COLOR[STORE_TYPES.COVA],
  [STORE_TYPES.GREENLINE]: STORE_BADGE_COLOR[STORE_TYPES.GREENLINE],
  [STORE_TYPES.FLOWHUB]: '#009961',
  [STORE_TYPES.LIGHTSPEED]: '#E4002B',
}

interface PosBadgeProps {
  storeType?: string
  /** Store's company id — when present, the badge is hidden. */
  companyId?: string | number | boolean | null
  /** Mock/demo store flag — when true, the badge is hidden. */
  useMockData?: boolean
}

/**
 * Colored text badge (e.g. "Cova"/"Greenline") shown in the pantry sidebar
 * header next to the store name, matching the legacy store badge. Renders a
 * leading "-" separator so the badge reads as "<store name> - <POS>". Only the
 * four POS-backed store types render; every other store type renders nothing
 * (so no dangling separator appears). Company stores (any `companyId`) and
 * mock-data stores are also suppressed.
 */
const PosBadge = ({ storeType, companyId, useMockData }: PosBadgeProps) => {
  if (!companyId || useMockData) return null
  const backgroundColor = storeType ? POS_BADGE_COLORS[storeType] : undefined
  const label = storeType ? STORE_SHORT_TYPE_NAMES[storeType] : undefined
  if (!backgroundColor || !label) return null

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
      }}
    >
      <span
        style={{ color: pantryColors.gray[400], fontSize: 9, lineHeight: 1 }}
      >
        -
      </span>
      <span
        style={{
          display: 'inline-block',
          padding: '0 4px',
          borderRadius: 3,
          fontSize: 8,
          fontWeight: 600,
          lineHeight: '12px',
          color: '#fff',
          backgroundColor,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </span>
  )
}

export default PosBadge
